<template>
  <div class="content">
    <div class="header d-flex justify-content-between align-items-center mb-4">
      <div class="title">
        <h3 class="h3 mb-n2 font-weight-bold">Painel FORMP&D</h3>
        <span class="subtitle-2 grey--text text--darken-1">Gerenciamento de Documentos do Painel FORMP&D</span>
      </div>
      <div class="actions d-flex flex-row align-center">
        <!-- A planilha de acompanhamento traz todas as empresas de um ano-base num unico
             arquivo, entao a importacao nao depende da empresa selecionada no topo e e so
             de usuario Galapos (PainelFormPDImportPolicy no back). -->
        <v-btn
          v-if="canImport"
          class="elevation-4 mr-2"
          color="primary"
          outlined
          data-testid="btn-importar-painel-formpd"
          @click="openImportModal"
        >
          <v-icon left dark>mdi-file-upload-outline</v-icon>
          <span style="font-size: 12px">Importar planilha</span>
        </v-btn>
        <v-btn class="elevation-4 mr-2" @click="refreshAll" outlined>
          <v-icon left dark>mdi-refresh</v-icon>
          <span style="font-size: 12px">Atualizar</span>
        </v-btn>
      </div>
    </div>

    <v-row align="center" class="mt-2 mb-4 mx-0">
      <v-col cols="12" sm="3" md="2">
        <year-select 
          v-model="filters.anoBase" 
          @change="refreshAll"
          outlined 
          dense 
          hide-details
        />
      </v-col>
      <v-col cols="12" sm="6" md="4">
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          placeholder="Buscar por título ou arquivo..."
          hide-details
          outlined
          dense
        />
      </v-col>
      <v-spacer />
      <v-col cols="auto" class="d-flex align-center">
        <v-btn 
          v-if="clientId"
          class="elevation-4 mr-2" 
          color="info" 
          @click="goToPareceresConsolidadoTodos"
        >
          <v-icon left>mdi-view-list</v-icon>
          <span style="font-size: 12px">Visualizar consolidado</span>
        </v-btn>
        <v-btn 
          v-if="clientId && canAdd"
          class="elevation-4" 
          color="primary" 
          style="background-color: #1c1c44;" 
          @click="openAddModal"
        >
          <v-icon left>mdi-plus</v-icon>
          <span style="font-size: 12px">Adicionar</span>
        </v-btn>
      </v-col>
    </v-row>

    <div class="body">
      <!-- Empty state when no client is selected -->
      <div v-if="!clientId" class="text-center mt-10">
        <v-icon size="64" color="grey lighten-1">mdi-domain-off</v-icon>
        <h4 class="mt-4 grey--text text--darken-1">Nenhum dado a ser listado</h4>
        <p class="grey--text">Selecione uma empresa no topo para visualizar ou adicionar documentos.</p>
      </div>

      <div v-else>
        <div v-for="instancia in instanciasList" :key="instancia.id" class="mb-6">
          <div class="d-flex justify-content-between align-items-center m-3">
            <span class="h5 mb-0 font-weight-bold">{{ instancia.name }}</span>
          </div>
          
          <div class="teros-elevation rounded">
            <v-data-table
              class="mb-6"
              :headers="computedHeaders"
              :items="items[instancia.id]"
              :search="search"
              :options.sync="options[instancia.id]"
              :loading="loading[instancia.id]"
              :footer-props="{
                'items-per-page-options': [10, 15, 30, 50, -1],
                'items-per-page-text': 'Itens por página'
              }"
            >
              <template v-slot:item.anexo="{ item }">
                <v-btn
                  v-if="item.anexo"
                  icon
                  small
                  color="primary"
                  :href="item.anexo"
                  target="_blank"
                  title="Baixar Arquivo"
                >
                  <v-icon>mdi-download</v-icon>
                </v-btn>
              </template>

              <template v-slot:item.acoes="{ item }">
                <v-btn v-if="item.tipo_documento === 2" icon small color="info" @click="goToPareceres(item)" title="Pareceres MCTI">
                  <v-icon>mdi-file-certificate</v-icon>
                </v-btn>
                <v-btn v-if="canDelete" icon small color="error" @click="confirmDelete(item, instancia.id)" title="Remover">
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </template>
            </v-data-table>
          </div>
        </div>
      </div>
    </div>

    <!-- Single Modal for all types -->
    <FormModal
      :max-width="600"
      :title="'Adicionar Documento'"
      :opened.sync="isVisibleFormModal"
      :value.sync="formModalData"
      :errorMessage="errorMessage"
      :cols="colsForm"
      :opts="opts"
      cancel-button-color="white"
      @save="doSave"
    />

    <v-dialog v-model="isVisibleImportModal" max-width="620" persistent>
      <v-card data-testid="modal-importar-painel-formpd">
        <v-card-title class="headline">Importar planilha do Painel FORMP&D</v-card-title>

        <v-card-text>
          <p class="mb-2">
            Selecione a planilha de acompanhamento (.xlsx). O arquivo contém todas as
            empresas de um ano-base &mdash; o ano é lido da própria planilha (célula B2),
            não da empresa selecionada no topo.
          </p>

          <v-alert dense text type="info" class="body-2">
            Os pareceres já devem estar cadastrados na tela (documento do tipo
            <b>Parecer</b>, com o PDF anexado) para cada empresa, ano-base e instância.
            A importação preenche os dados desses registros; linhas sem o parecer
            cadastrado são reportadas como aviso e não interrompem as demais.
          </v-alert>

          <v-file-input
            v-model="importFile"
            accept=".xlsx"
            label="Planilha de acompanhamento (.xlsx)"
            prepend-icon="mdi-microsoft-excel"
            show-size
            outlined
            dense
            hide-details="auto"
            class="mt-4"
            data-testid="input-arquivo-painel-formpd"
            :error-messages="importErrorMessage"
          />
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="importing" data-testid="btn-cancelar-importacao" @click="closeImportModal">
            Cancelar
          </v-btn>
          <v-btn
            color="primary"
            style="background-color: #1c1c44;"
            :loading="importing"
            :disabled="!importFile"
            data-testid="btn-confirmar-importacao"
            @click="doImport"
          >
            Importar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import yearsOptions from "@/helpers/yearsOptions";
import { userHasAccess } from "@/helpers/permissionValidator";
import { AnyAdmin } from "@/core/enums/user-types";

export default {
  name: "PrestacaoContasList",
  components: {
    FormModal: () => import("@/components/form-modal.vue"),
    YearSelect: () => import("@/components/year-select.vue")
  },
  data() {
    return {
      filters: {
        anoBase: new Date().getFullYear().toString()
      },
      instanciasList: [
        { id: 1, name: "FORMP&D" },
        { id: 2, name: "Contestação" },
        { id: 3, name: "Recurso Administrativo" }
      ],
      tiposDocumentoList: [
        { id: 1, name: "Defesa" },
        { id: 2, name: "Parecer" }
      ],
      search: "",
      isVisibleFormModal: false,
      isVisibleImportModal: false,
      importFile: null,
      importing: false,
      importErrorMessage: "",
      items: { 1: [], 2: [], 3: [] },
      totalItems: { 1: 0, 2: 0, 3: 0 },
      loading: { 1: false, 2: false, 3: false },
      options: { 
        1: { page: 1, itemsPerPage: 15 }, 
        2: { page: 1, itemsPerPage: 15 }, 
        3: { page: 1, itemsPerPage: 15 } 
      },
      formModalData: {},
      errorMessage: "",
      opts: {
        years: yearsOptions.filter(y => parseInt(y.value) <= new Date().getFullYear()),
        instanciasProps: [
          { id: 1, name: "FORMP&D" },
          { id: 2, name: "Contestação" },
          { id: 3, name: "Recurso Administrativo" }
        ],
        tiposProps: [
          { id: 1, name: "Defesa" },
          { id: 2, name: "Parecer" }
        ]
      },
      cols: [
        { value: "id", text: "ID", align: "start", sortable: false, width: "15px" },
        { value: "ano_base", text: "Ano-base", align: "center", sortable: false, width: "100px" },
        { value: "titulo", text: "Título/Arquivo", align: "start", sortable: false},
        { value: "anexo", text: "Baixar", align: "center", sortable: false, width: "100px" },
        { value: "acoes", text: "Ações", align: "center", sortable: false, width: "100px" }
      ],
      colsForm: [
        {
          key: "ano_base",
          name: "Ano Base",
          type: this.$fieldTypes.SELECT,
          rel: { to: "years", key: "value", name: "text" },
          rules: [{ rule: "required" }],
          colSize: 4
        },
        {
          key: "instancia",
          name: "Instância de Análise",
          type: this.$fieldTypes.SELECT,
          rel: { to: "instanciasProps", key: "id", name: "name" },
          rules: [{ rule: "required" }],
          colSize: 8
        },
        {
          key: "tipo_documento",
          name: "Tipo de Documento",
          type: this.$fieldTypes.SELECT,
          rel: { to: "tiposProps", key: "id", name: "name" },
          rules: [{ rule: "required" }],
          colSize: 12
        },
        {
          key: "arquivo",
          name: "Arquivo (.pdf)",
          type: this.$fieldTypes.FILE2,
          rules: [{ rule: "required" }],
          colSize: 12
        }
      ]
    };
  },
  computed: {
    ...mapGetters(["clientId", "user"]),
    canAdd() {
      return userHasAccess(this.user, 'Cadastro.prestacaoContasPareceres.create');
    },
    canImport() {
      return AnyAdmin.includes(this.user?.tipo_usuario) && this.canAdd;
    },
    canDelete() {
      return userHasAccess(this.user, 'Cadastro.prestacaoContasPareceres.delete');
    },
    computedHeaders() {
      return this.cols.map(h => ({
        ...h,
        class: `vuetify-table-header${
          this.getClient && this.getClient().isClient ? "-cli" : ""
        }`
      }));
    }
  },
  mounted() {
    if (this.$route.query.anoBase) {
      this.filters.anoBase = this.$route.query.anoBase.toString();
    }
    this.refreshAll();
  },
  watch: {
    user: {
      deep: true,
      handler(newUser) {
        if (!userHasAccess(newUser, 'Cadastro.prestacaoContasPareceres.index')) {
          this.$router.push({ path: '/home' });
        }
      }
    },
    clientId() {
      if (!userHasAccess(this.user, 'Cadastro.prestacaoContasPareceres.index')) {
        this.$router.push({ path: '/home' });
        return;
      }
      this.refreshAll();
    }
  },
  methods: {
    refreshAll() {
      if (!this.clientId) {
        this.items = { 1: [], 2: [], 3: [] };
        return;
      }
      this.instanciasList.forEach(inst => {
        this.fetchData(inst.id);
      });
    },
    fetchData(instanciaId) {
      if (!this.clientId) return;
      
      this.loading[instanciaId] = true;
      const resource = this.apiResource(`v1/cadastro/${this.clientId}/prestacao-contas`);
      
      const queryParams = {
        'filters[instancia]': instanciaId,
        'filters[ano_base]': this.filters.anoBase,
        per_page: 999
      };

      const qs = new URLSearchParams(queryParams).toString();
      
      resource.get({ query: qs }).then(res => {
        let data = res.data || [];
        // Sort by tipo_documento: 1 (Defesa) comes before 2 (Parecer)
        data.sort((a, b) => a.tipo_documento - b.tipo_documento);
        
        this.items[instanciaId] = data;
        this.totalItems[instanciaId] = res.total || 0;
      }).catch(() => {
        this.items[instanciaId] = [];
        this.totalItems[instanciaId] = 0;
      }).finally(() => {
        this.loading[instanciaId] = false;
      });
    },
    openImportModal() {
      this.importFile = null;
      this.importErrorMessage = "";
      this.isVisibleImportModal = true;
    },
    closeImportModal() {
      this.isVisibleImportModal = false;
      this.importFile = null;
      this.importErrorMessage = "";
    },
    doImport() {
      if (!this.importFile) {
        this.importErrorMessage = "Selecione a planilha (.xlsx).";
        return;
      }

      if (!/\.xlsx$/i.test(this.importFile.name)) {
        this.importErrorMessage = "A planilha precisa estar no formato .xlsx.";
        return;
      }

      this.importing = true;
      this.importErrorMessage = "";

      const formData = new FormData();
      formData.append("arquivo", this.importFile);

      // Endpoint global (sem empresaId): a planilha cobre todas as empresas do ano-base.
      this.apiResource("v1/cadastro/painel-formpd/importar")
        .save(formData)
        .then(response => {
          // A importacao roda em fila; o resultado (inclusive os avisos por linha) chega
          // depois, pela notificacao do sistema.
          this.$notify({
            group: "geral",
            type: "success",
            title: "Importação enviada",
            text:
              (response && response.message) ||
              "Importação adicionada à fila! Você receberá uma notificação com o resultado."
          });

          this.closeImportModal();
          this.refreshAll();
        })
        .catch(err => {
          const data = err.response?.data;
          this.importErrorMessage =
            data?.errors?.arquivo?.[0] ||
            data?.error ||
            data?.message ||
            "Erro ao enviar a planilha.";
        })
        .finally(() => {
          this.importing = false;
        });
    },
    openAddModal() {
      this.formModalData = {
        ano_base: this.filters.anoBase ? parseInt(this.filters.anoBase, 10) : null,
        instancia: null,
        tipo_documento: null,
        arquivo: [],
        empresa_id: this.clientId
      };
      this.errorMessage = "";
      this.isVisibleFormModal = true;
    },
    editItem(item) {
      this.errorMessage = "";
      this.formModalData = { 
        ...item,
        ano_base: item.ano_base ? parseInt(item.ano_base, 10) : null
      };
      this.isVisibleFormModal = true;
    },
    goToPareceres(item) {
      this.$router.push({
        name: 'listagem-pareceres-mcti',
        params: { prestacaoId: item.id },
        query: { instancia: item.instancia, anoBase: this.filters.anoBase }
      });
    },
    goToPareceresConsolidadoTodos() {
      this.$router.push({
        name: 'listagem-pareceres-mcti',
        params: { prestacaoId: '0' },
        query: { anoBase: this.filters.anoBase }
      });
    },
    doSave(data) {
      if (!data.arquivo || !data.arquivo.length) {
        this.errorMessage = "O arquivo é obrigatório.";
        return;
      }

      const resource = this.apiResource(`v1/cadastro/${this.clientId}/prestacao-contas`);
      const formData = new FormData();
      
      formData.append("ano_base", data.ano_base);
      formData.append("instancia", data.instancia);
      formData.append("tipo_documento", data.tipo_documento);
      formData.append("empresa_id", this.clientId);
      // Default to Lei do Bem
      formData.append("trabalho", 1); 
      formData.append("anexo", data.arquivo[0]);

      resource.save(formData).then(() => {
        this.$notify({
          group: "geral",
          type: "success",
          title: "Sucesso",
          text: "Documento salvo com sucesso!"
        });
        
        this.isVisibleFormModal = false;
        this.fetchData(data.instancia);
      }).catch(err => {
        this.errorMessage = err.response?.data?.error
          || Object.values(err.response?.data?.errors || {}).flat()[0]
          || err.response?.data?.message
          || "Erro ao salvar documento.";
      });
    },
    confirmDelete(row, instanciaId) {
      const warningMsg = row.tipo_documento === 2 
        ? '<br><br><span class="warning--text font-weight-bold">Atenção: Todos os registros de avaliações do MCTI vinculados a este Parecer também serão removidos.</span>'
        : '';

      this.showConfirm(
        "Confirmação",
        `Deseja realmente remover este documento?${warningMsg}`,
        () => {
          const resource = this.apiResource(`v1/cadastro/${this.clientId}/prestacao-contas`);
          resource.delete(row.id).then(() => {
            this.$notify({
              group: "geral",
              type: "success",
              title: "Removido",
              text: "Documento removido com sucesso!"
            });
            this.fetchData(instanciaId);
          });
        }
      );
    }
  }
};
</script>

<style scoped>
.content {
  padding: 1.5rem;
}
.header {
  padding-bottom: 15px;
}
</style>
